import { buildProps, definePropType } from '@hongluan-ui/utils'
import { popperContentProps } from '@hongluan-ui/components/popper'
import {
  useDelayedToggleProps,
  useAriaProps,
} from '@hongluan-ui/hooks'
import type { ExtractPropTypes } from 'vue'
import type { MaybeElementRef } from '@vueuse/core'
import type TooltipContent from './content.vue'

export const useTooltipContentProps = buildProps({
  ...useDelayedToggleProps,
  ...popperContentProps,
  /**
   * @description which element the tooltip CONTENT appends to
   */
  appendTo: {
    type: definePropType<string | HTMLElement>([String, Object]),
  },
  /**
   * @description display content, can be overridden by `slot#content`
   */
  content: {
    type: String,
    default: '',
  },
  /**
   * @description whether `content` is treated as HTML string
   */
  rawContent: Boolean,
  /**
   * @description when tooltip inactive and `persistent` is `false` , popconfirm will be destroyed
   */
  persistent: Boolean,
  // because model toggle prop is generated dynamically
  // so the typing cannot be evaluated by typescript as type:
  // [name]: { type: Boolean, default: null }
  // so we need to declare that again for type checking.
  /**
   * @description visibility of Tooltip
   */
  visible: {
    type: definePropType<boolean | null>(Boolean),
    default: null,
  },
  /**
   * @description animation name
   */
  transition: {
    type: String,
    default: 'fade',
  },
  /**
   * @description whether tooltip content is teleported, if `true` it will be teleported to where `append-to` sets
   */
  teleported: {
    type: Boolean,
    default: true,
  },
  /**
   * @description whether Tooltip is disabled
   */
  disabled: Boolean,
  outsideIgnore: {
    type: definePropType<MaybeElementRef[]>(Array),
    default: () => [],
  },
  ...useAriaProps(['ariaLabel']),
} as const)

export type TooltipContentProps = ExtractPropTypes<
  typeof useTooltipContentProps
>

export type TooltipContentInstance = InstanceType<typeof TooltipContent>
